import { useContext } from 'react';
import PropTypes from 'prop-types';

import { rulesV2 } from './RBACRules';
import tips from './DataTips';
import {
  getPermissionForResource,
  hasReadAccessToResource,
} from './AccessTools';
import {
  ASSET_MODEL,
  ASSET_MODEL_READ,
  DELETE,
  REMOTE,
  REMOTE_EXECUTE,
  REPORT_CREATE,
  UPDATE,
} from '../Constants/RBAC';
import { PROVISIONING_MODEL_ORGID } from '../Constants/StaticUUIDs';
import OrgsMapContext from '../Context/OrgsMapContext';
import RootOrgContext from '../Context/RootOrgContext';

export function checkV2({
  action,
  orgID,
  orgsMap,
  resource,
  rootOrgID,
  sharedGtemplate,
  sharedUserProfile,
}) {
  let permissionOrgID = orgID;
  if (sharedGtemplate?.orgID) {
    // if editLocked use sharedGtemplate.orgID for write actions
    const isWriteAction = action.includes(UPDATE) || action.includes(DELETE);
    if (sharedGtemplate.editLocked && isWriteAction) {
      permissionOrgID = sharedGtemplate.orgID;
    } else {
      // get the user's rootOrg path
      const { path = '' } = orgsMap?.[rootOrgID] || {};
      // if the sharedGtemplate is from an org somewhere in the path
      // use the user's rootOrgID for the permissions check of asset_model
      if (path?.includes?.(sharedGtemplate?.orgID)) permissionOrgID = rootOrgID;
    }
  }

  // sharedUserProfile only passed in from src/Components/Organization/UserProfileActionDropdown from the Edit toggle wrapper
  // there is no individual READ op to consider and only profile owning org can delete
  if (sharedUserProfile?.shared && !sharedUserProfile?.editLocked) {
    // get the user's rootOrg path
    const { path = '' } = orgsMap?.[rootOrgID] || {};
    // if the sharedUserProfile is from an org somewhere in the path
    // use the user's rootOrgID for the permissions check of user
    if (path?.includes?.(orgID)) permissionOrgID = rootOrgID;
  }

  const permission = getPermissionForResource(permissionOrgID, resource);
  if (!permission) return false;

  return rulesV2
    .get(Number.parseInt(permission, 10))
    ?.get?.(resource)
    ?.has?.(action);
}

export function tip(dataTips, action, data) {
  const staticDataTips = dataTips.static;

  if (staticDataTips && staticDataTips[action]) {
    // static data-tip provided for action
    return staticDataTips[action];
  }

  const dynamicDataTips = dataTips.dynamic;

  if (dynamicDataTips) {
    const dataTipCondition = dynamicDataTips[action];
    if (!dataTipCondition) {
      // dynamic data-tip not provided for action
      return false;
    }
    // dynamic data-tip provided for action
    return dataTipCondition(data);
  }

  // no data-tips provided
  return false;
}

function Can({
  data = {},
  no,
  orgID,
  perform,
  resource,
  sharedGtemplate,
  sharedUserProfile,
  yes,
}) {
  const orgsMap = useContext(OrgsMapContext);
  const { _id: rootOrgID } = useContext(RootOrgContext);

  if (perform === ASSET_MODEL_READ && orgID === PROVISIONING_MODEL_ORGID) {
    return yes();
  }
  if (
    perform === REPORT_CREATE &&
    !hasReadAccessToResource(orgID, ASSET_MODEL)
  ) {
    return no();
  }

  // checks user permissions
  const isAuthorized = checkV2({
    action: perform,
    orgID,
    orgsMap,
    resource,
    rootOrgID,
    sharedGtemplate,
    sharedUserProfile,
  });

  // extra check to make sure org is enabled for remote actions
  const currentOrgRemote = orgsMap?.[orgID]?.features?.remote;
  const orgRemoteCheck = resource !== REMOTE || currentOrgRemote;

  // extra check to make sure that gateway is remote enabled for execute
  const gatewayRemoteCheck = perform !== REMOTE_EXECUTE || data?.assetRemote;

  if (isAuthorized && orgRemoteCheck && gatewayRemoteCheck) return yes();

  const conditionalRemoteTooltipData =
    resource === REMOTE ? { currentUserRemote: isAuthorized } : {};
  const dataTip = tip(tips, perform, {
    ...data,
    currentOrgRemote,
    ...conditionalRemoteTooltipData,
  });
  return dataTip ? no(dataTip) : no();
}

Can.propTypes = {
  data: PropTypes.any,
  no: PropTypes.func,
  orgID: PropTypes.string,
  perform: PropTypes.string,
  resource: PropTypes.string,
  sharedGtemplate: PropTypes.shape({
    editLocked: PropTypes.bool,
    orgID: PropTypes.string,
  }),
  yes: PropTypes.func,
};

Can.defaultProps = { no: () => null, yes: () => null };

export default Can;
